"use client";

import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, PlusSquare, User, LogOut, Menu, Users, Briefcase, Handshake, UsersIcon, Settings, ScrollText, Shield, ClipboardCheck, ToggleLeft, Layers } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { logout } from "@/lib/actions/auth";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { getDashboardBasePath } from "@/lib/routes";

function SidebarLinks({ role, enabledFeatures = [], onNavigate }: { role: string; enabledFeatures?: string[]; onNavigate?: () => void }) {
  const { pathname } = useLocation();
  const base = getDashboardBasePath(role);

  const baseLinks = [
    { href: base, label: "Dashboard", icon: LayoutDashboard },
    { href: `${base}/posts`, label: "Blog Posts", icon: FileText },
    { href: `${base}/posts/new`, label: "New Post", icon: PlusSquare },
    { href: `${base}/profile`, label: "Profile", icon: User },
  ];

  const superAdminLinks = [
    { href: `${base}/admins`, label: "Admin Profiles", icon: Shield, featureKey: "admins" },
    { href: `${base}/blog-review`, label: "Blog Review", icon: ClipboardCheck, featureKey: "blog_review" },
    { href: `${base}/users`, label: "Users & Profiles", icon: UsersIcon, featureKey: "users" },
    { href: `${base}/jobs`, label: "Jobs Management", icon: Briefcase, featureKey: "jobs" },
    { href: `${base}/referrals`, label: "Referral Requests", icon: Handshake, featureKey: "referrals" },
    { href: `${base}/waitlist`, label: "Waitlist", icon: Users, featureKey: "waitlist" },
    { href: `${base}/settings`, label: "Platform Settings", icon: Settings, featureKey: "settings" },
    { href: `${base}/audit`, label: "Audit Logs", icon: ScrollText, featureKey: "audit" },
    { href: `${base}/features`, label: "Features", icon: ToggleLeft, featureKey: "__super_only__" },
    { href: `${base}/dashboard-features`, label: "Dashboard Features", icon: Layers, featureKey: "__super_only__" },
  ];

  let links;
  if (role === "super_admin") {
    links = [...baseLinks, ...superAdminLinks];
  } else {
    // Show base links + any super admin links whose featureKey is enabled for this admin
    const grantedLinks = superAdminLinks.filter(
      (link) => link.featureKey !== "__super_only__" && enabledFeatures.includes(link.featureKey)
    );
    links = [...baseLinks, ...grantedLinks];
  }

  // Longest href wins so "/dashboard" is not active on every "/dashboard/*" route.
  const activeHref = links.reduce<string | null>((best, link) => {
    const matches =
      pathname === link.href || (pathname.startsWith(link.href) && pathname.charAt(link.href.length) === "/");
    if (!matches) return best;
    if (!best || link.href.length > best.length) return link.href;
    return best;
  }, null);

  return (
    <nav className="space-y-1">
      {links.map((link) => {
        const Icon = link.icon;
        const isActive = link.href === activeHref;
        return (
          <Link
            key={link.href}
            to={link.href}
            onClick={onNavigate}
            className={cn(
              "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition",
              isActive
                ? "bg-zinc-900 text-white ring-1 ring-white/10"
                : "text-slate-400 hover:bg-zinc-950 hover:text-white"
            )}
          >
            <Icon className="h-4 w-4" />
            {link.label}
          </Link>
        );
      })}
    </nav>
  );
}

function SidebarInner({ userEmail, fullName, role, enabledFeatures = [], onNavigate }: { userEmail: string; fullName: string; role: string; enabledFeatures?: string[]; onNavigate?: () => void }) {
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const handleLogout = async () => {
    await logout();
    await refresh();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="flex h-full min-h-screen flex-col bg-black">
      <div className="border-b border-white/5 px-4 py-5">
        <h1 className="text-lg font-bold text-white">ReffConnect Admin</h1>
        {role === "super_admin" && (
          <span className="mt-1 block text-xs font-medium text-purple-400">Super Admin Access</span>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-4">
        <SidebarLinks role={role} enabledFeatures={enabledFeatures} onNavigate={onNavigate} />
      </div>
      <div className="space-y-3 border-t border-white/5 px-3 py-4">
        <div className="rounded-lg bg-slate-900/50 px-3 py-3 ring-1 ring-white/5">
          <p className="truncate text-sm font-medium text-white">{fullName}</p>
          <p className="truncate text-xs text-slate-400">{userEmail}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => void handleLogout()}
          className="w-full justify-start border-white/10 bg-slate-900/50 text-slate-300 hover:bg-slate-900 hover:text-white"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </Button>
      </div>
    </div>
  );
}

export function Sidebar({ userEmail, fullName, role = "admin", enabledFeatures = [] }: { userEmail: string; fullName: string; role?: string; enabledFeatures?: string[] }) {
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden w-64 flex-shrink-0 border-r border-white/5 bg-black md:block">
        <SidebarInner userEmail={userEmail} fullName={fullName} role={role} enabledFeatures={enabledFeatures} />
      </aside>

      {/* Mobile top bar with hamburger */}
      <div className="fixed inset-x-0 top-0 z-30 flex h-14 items-center gap-3 border-b border-white/5 bg-black/95 px-4 backdrop-blur-sm md:hidden">
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-white/10 bg-black/80 hover:bg-black">
            <Menu className="h-5 w-5 text-white" />
          </SheetTrigger>
          <SheetContent side="left" className="w-[88vw] max-w-xs border-white/5 bg-black p-0">
            <SidebarInner userEmail={userEmail} fullName={fullName} role={role} enabledFeatures={enabledFeatures} onNavigate={() => setSheetOpen(false)} />
          </SheetContent>
        </Sheet>
        <span className="text-sm font-bold text-white">ReffConnect Admin</span>
        {role === "super_admin" && (
          <span className="rounded-full bg-purple-500/10 px-2 py-0.5 text-[10px] font-medium text-purple-400 ring-1 ring-purple-500/30">
            Super
          </span>
        )}
      </div>
    </>
  );
}
